/**
 * React Query hooks for the Store Info > Photos and Documents page, all under
 * /v1/stores/info/{store_no}/folders and /files and gated by the same
 * stores.info capability as the parent page (api#698):
 *
 *   GET    .../folders?parent_id=     -> { breadcrumb, folders, files }
 *   POST   .../folders               -> create (duplicate name = conflict)
 *   PATCH  .../folders/{id}          -> rename
 *   GET    .../folders/{id}/count    -> recursive { folders, files }
 *   DELETE .../folders/{id}          -> recursive delete, returns the counts
 *   POST   .../files                 -> up to 50 presigned PUT slots
 *   POST   .../files/{id}/complete   -> pending -> ready
 *   GET    .../files/{id}/url        -> 15-minute presigned GET
 *   PATCH  .../files/{id}            -> rename and/or move
 *   DELETE .../files/{id}
 *   GET/POST .../files/{id}/notes    -> append-only, newest first
 *
 * Same conventions as useStoreInfo.ts: INFO_API_BASE for the path, no retries,
 * and a 404 or 503 means "not deployed yet" so the page shows the quiet
 * NotAvailableCard rather than an error.
 *
 * The byte transfer itself does NOT go through apiFetch: the presigned PUT
 * goes straight to S3 with no session cookie, and it runs on XMLHttpRequest
 * rather than fetch because only XHR reports upload progress.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost, type ApiError } from './api';
import { STORES_API_BASE } from './useStores';

const INFO_API_BASE = `${STORES_API_BASE}/info`;

/** The presign POST takes at most 50 entries per call (api#698). */
export const UPLOAD_BATCH_SIZE = 50;

export type StoreFolderCrumb = { id: number; name: string };

export type StoreFolder = {
  id: number;
  name: string;
  created_by: string;
  created_at: string;
  updated_at: string;
};

export type StoreFileRow = {
  id: number;
  name: string;
  content_type: string | null;
  size_bytes: number | null;
  uploaded_by: string;
  created_at: string;
  note_count: number;
};

/** One level of the tree: where you are, the folders here, the files here. */
export type StoreFolderListing = {
  breadcrumb: StoreFolderCrumb[];
  folders: StoreFolder[];
  files: StoreFileRow[];
};

export type StoreFileNote = {
  id: number;
  body: string;
  author_email: string;
  created_at: string;
};

export type StoreFolderCount = { folders: number; files: number };

function folderKey(storeNo: string | null, folderId: number | null): unknown[] {
  return ['stores', 'info', 'folders', storeNo, folderId];
}

export function storeFolderQueryKey(storeNo: string | null, folderId: number | null): unknown[] {
  return folderKey(storeNo, folderId);
}

function listingPath(storeNo: string, folderId: number | null): string {
  const query = folderId == null ? '' : `?parent_id=${folderId}`;
  return `${INFO_API_BASE}/${storeNo}/folders${query}`;
}

/**
 * A duplicate folder name in the same parent. The contract calls it
 * "conflict" without pinning the wire shape, so a 409 and a body or message
 * that says conflict both count: folder upload merges into the existing
 * folder instead of failing when either comes back.
 */
export function isFolderConflict(error: unknown): boolean {
  const err = error as ApiError | undefined;
  if (!err) return false;
  if (err.status === 409) return true;
  const body = err.body;
  const text =
    typeof body === 'string'
      ? body
      : body && typeof body === 'object'
        ? JSON.stringify(body)
        : err.message ?? '';
  return text.toLowerCase().includes('conflict');
}

export function useStoreFolderListing(
  storeNo: string | null,
  folderId: number | null,
  enabled: boolean,
): UseQueryResult<StoreFolderListing, Error> {
  return useQuery<StoreFolderListing, Error>({
    queryKey: folderKey(storeNo, folderId),
    queryFn: async () => {
      const r = await apiGet<Partial<StoreFolderListing>>(listingPath(storeNo as string, folderId));
      return { breadcrumb: r.breadcrumb ?? [], folders: r.folders ?? [], files: r.files ?? [] };
    },
    enabled: enabled && !!storeNo,
    staleTime: 15_000,
    retry: false,
  });
}

/** Reads one level outside React Query, for the folder-upload tree walk. */
export function fetchStoreFolderListing(
  storeNo: string,
  folderId: number | null,
): Promise<StoreFolderListing> {
  return apiGet<StoreFolderListing>(listingPath(storeNo, folderId));
}

export type CreateFolderInput = { name: string; parent_id: number | null };

export function createStoreFolder(storeNo: string, input: CreateFolderInput): Promise<StoreFolder> {
  return apiPost<StoreFolder>(`${INFO_API_BASE}/${storeNo}/folders`, input);
}

export function useCreateStoreFolder(
  storeNo: string | null,
): UseMutationResult<StoreFolder, Error, CreateFolderInput> {
  const qc = useQueryClient();
  return useMutation<StoreFolder, Error, CreateFolderInput>({
    mutationFn: (input) => createStoreFolder(storeNo as string, input),
    onSuccess: (_folder, input) => {
      void qc.invalidateQueries({ queryKey: folderKey(storeNo, input.parent_id) });
    },
  });
}

export type RenameFolderInput = { id: number; name: string; parent_id: number | null };

export function useRenameStoreFolder(
  storeNo: string | null,
): UseMutationResult<StoreFolder, Error, RenameFolderInput> {
  const qc = useQueryClient();
  return useMutation<StoreFolder, Error, RenameFolderInput>({
    mutationFn: ({ id, name }) =>
      apiPatch<StoreFolder>(`${INFO_API_BASE}/${storeNo}/folders/${id}`, { name }),
    onSuccess: (_folder, input) => {
      void qc.invalidateQueries({ queryKey: folderKey(storeNo, input.parent_id) });
    },
  });
}

/**
 * How much a folder delete would take with it. The delete modal holds its
 * button disabled until this lands, so nobody confirms a recursive delete
 * without seeing the numbers.
 */
export function useStoreFolderCount(
  storeNo: string | null,
  folderId: number | null,
): UseQueryResult<StoreFolderCount, Error> {
  return useQuery<StoreFolderCount, Error>({
    queryKey: ['stores', 'info', 'folder-count', storeNo, folderId],
    queryFn: () =>
      apiGet<StoreFolderCount>(`${INFO_API_BASE}/${storeNo}/folders/${folderId}/count`),
    enabled: !!storeNo && folderId != null,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

export function useDeleteStoreFolder(
  storeNo: string | null,
): UseMutationResult<StoreFolderCount, Error, { id: number; parent_id: number | null }> {
  const qc = useQueryClient();
  return useMutation<StoreFolderCount, Error, { id: number; parent_id: number | null }>({
    mutationFn: ({ id }) => apiDelete<StoreFolderCount>(`${INFO_API_BASE}/${storeNo}/folders/${id}`),
    onSuccess: (_counts, input) => {
      void qc.invalidateQueries({ queryKey: folderKey(storeNo, input.parent_id) });
    },
  });
}

export type UploadSlotRequest = {
  name: string;
  content_type: string;
  size_bytes: number;
  folder_id: number | null;
};

export type UploadSlot = { file_id: number; key: string; upload_url: string };

/** One presign POST: up to UPLOAD_BATCH_SIZE slots, in request order. */
export async function requestUploadSlots(
  storeNo: string,
  files: UploadSlotRequest[],
): Promise<UploadSlot[]> {
  const r = await apiPost<{ files?: UploadSlot[] }>(`${INFO_API_BASE}/${storeNo}/files`, { files });
  return r.files ?? [];
}

export function completeUpload(storeNo: string, fileId: number): Promise<void> {
  return apiPost<void>(`${INFO_API_BASE}/${storeNo}/files/${fileId}/complete`, {});
}

/**
 * PUTs the bytes straight to S3. XMLHttpRequest, not fetch, because fetch
 * cannot report upload progress and the panel shows a bar per file. No
 * credentials: the presigned URL carries its own authorization, and sending
 * the session cookie to S3 would break the signature.
 */
export function putWithProgress(
  uploadUrl: string,
  file: File,
  onProgress: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    if (file.type) xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        resolve();
      } else {
        reject(new Error(`Upload failed (${xhr.status})`));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed: network error'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));
    xhr.send(file);
  });
}

/**
 * A 15-minute presigned GET, fetched on demand (thumbnail, Open, or Download).
 * With `download` the link is signed as an attachment under the file's display
 * name, so the browser saves it instead of showing it.
 */
export async function fetchStoreFileUrl(storeNo: string, fileId: number, download = false): Promise<string> {
  const r = await apiGet<{ url: string }>(
    `${INFO_API_BASE}/${storeNo}/files/${fileId}/url${download ? '?download=1' : ''}`,
  );
  return r.url;
}

export type PatchFileInput = {
  id: number;
  /** The folder the row is listed under now, so the right listing refreshes. */
  folder_id: number | null;
  name?: string;
  /** Present only on a move; null means the store's root. */
  move_to?: number | null;
};

/** Rename (display name only; the S3 key never changes) and/or move. */
export function usePatchStoreFile(
  storeNo: string | null,
): UseMutationResult<StoreFileRow, Error, PatchFileInput> {
  const qc = useQueryClient();
  return useMutation<StoreFileRow, Error, PatchFileInput>({
    mutationFn: ({ id, name, move_to }) => {
      const body: { name?: string; folder_id?: number | null } = {};
      if (name !== undefined) body.name = name;
      if (move_to !== undefined) body.folder_id = move_to;
      return apiPatch<StoreFileRow>(`${INFO_API_BASE}/${storeNo}/files/${id}`, body);
    },
    onSuccess: (_file, input) => {
      void qc.invalidateQueries({ queryKey: folderKey(storeNo, input.folder_id) });
      if (input.move_to !== undefined) {
        void qc.invalidateQueries({ queryKey: folderKey(storeNo, input.move_to) });
      }
    },
  });
}

export function useDeleteStoreFile(
  storeNo: string | null,
): UseMutationResult<void, Error, { id: number; folder_id: number | null }> {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: number; folder_id: number | null }>({
    mutationFn: ({ id }) => apiDelete<void>(`${INFO_API_BASE}/${storeNo}/files/${id}`),
    onSuccess: (_void, input) => {
      void qc.invalidateQueries({ queryKey: folderKey(storeNo, input.folder_id) });
    },
  });
}

export function useStoreFileNotes(
  storeNo: string | null,
  fileId: number | null,
): UseQueryResult<StoreFileNote[], Error> {
  return useQuery<StoreFileNote[], Error>({
    queryKey: ['stores', 'info', 'file-notes', storeNo, fileId],
    queryFn: async () => {
      const r = await apiGet<{ notes?: StoreFileNote[] }>(
        `${INFO_API_BASE}/${storeNo}/files/${fileId}/notes`,
      );
      return r.notes ?? [];
    },
    enabled: !!storeNo && fileId != null,
    staleTime: 15_000,
    retry: false,
  });
}

/** Append one note to a file. Like the store note log: no edit, no delete. */
export function useAddStoreFileNote(
  storeNo: string | null,
  fileId: number | null,
  folderId: number | null,
): UseMutationResult<StoreFileNote, Error, string> {
  const qc = useQueryClient();
  return useMutation<StoreFileNote, Error, string>({
    mutationFn: (body) =>
      apiPost<StoreFileNote>(`${INFO_API_BASE}/${storeNo}/files/${fileId}/notes`, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['stores', 'info', 'file-notes', storeNo, fileId] });
      // note_count lives on the listing row, so the badge follows the add.
      void qc.invalidateQueries({ queryKey: folderKey(storeNo, folderId) });
    },
  });
}
